import { Booking } from '@/types';

// Scoping of resources to a single occurrence of a recurring series.
//
// An occurrence record carries the series' `recurrenceGroupId`, so a resource
// attached to it is displayed with the series' date range and recurrence
// details. Resources added under the "Only This Booking" scope therefore get a
// standalone booking covering the occurrence's own dates instead.

const toIdList = (booking: Pick<Booking, 'resourceId' | 'resourceIds'>): string[] => {
  if (booking.resourceIds && booking.resourceIds.length > 0) return booking.resourceIds.map(String);
  if (booking.resourceId != null) return [String(booking.resourceId)];
  return [];
};

export interface OccurrenceResourceSplit {
  /** Resources already on the occurrence that the user kept. */
  keptResourceIds: string[];
  /** Resources the user added to this occurrence only. */
  addedResourceIds: string[];
}

/** Splits a submitted resource selection into the booking's existing resources and the added ones. */
export function splitOccurrenceResourceIds(
  originalBooking: Pick<Booking, 'resourceId' | 'resourceIds'>,
  submittedResourceIds: Array<string | number> | undefined,
): OccurrenceResourceSplit {
  const originalIds = toIdList(originalBooking);
  const submitted = (submittedResourceIds ?? []).map(String);
  const seen = new Set<string>();
  const keptResourceIds: string[] = [];
  const addedResourceIds: string[] = [];

  for (const id of submitted) {
    if (seen.has(id)) continue;
    seen.add(id);
    if (originalIds.includes(id)) keptResourceIds.push(id);
    else addedResourceIds.push(id);
  }

  return { keptResourceIds, addedResourceIds };
}

const pickResourceAssignments = (
  assignments: Booking['resourceAssignments'],
  resourceIds: string[],
): Booking['resourceAssignments'] => {
  if (!assignments) return undefined;
  const picked = Object.fromEntries(
    Object.entries(assignments).filter(([resourceId]) => resourceIds.includes(String(resourceId))),
  );
  return Object.keys(picked).length > 0 ? picked : undefined;
};

/** The submitted details with the added resources removed, for the occurrence record itself. */
export function buildOccurrenceUpdate(
  submitted: Partial<Booking>,
  split: OccurrenceResourceSplit,
): Partial<Booking> {
  const { keptResourceIds } = split;
  const update: Partial<Booking> = { ...submitted };
  update.resourceIds = keptResourceIds;
  update.resourceId = submitted.resourceId != null && keptResourceIds.includes(String(submitted.resourceId))
    ? String(submitted.resourceId)
    : keptResourceIds[0];
  const assignments = pickResourceAssignments(submitted.resourceAssignments, keptResourceIds);
  if (submitted.resourceAssignments) update.resourceAssignments = assignments;
  return update;
}

/** A standalone, non-recurring booking over the edited occurrence's dates for the added resources. */
export function buildOccurrenceScopedBooking(
  originalBooking: Booking,
  submitted: Partial<Booking>,
  addedResourceIds: string[],
): Omit<Booking, 'id'> {
  const merged = { ...originalBooking, ...submitted };
  const {
    id: _id,
    recurrence: _recurrence,
    recurrenceGroupId: _recurrenceGroupId,
    isRecurrenceParent: _isRecurrenceParent,
    parentBookingId: _parentBookingId,
    recurrencePending: _recurrencePending,
    ...rest
  } = merged;

  return {
    ...rest,
    resourceId: addedResourceIds[0],
    resourceIds: addedResourceIds,
    resourceAssignments: pickResourceAssignments(merged.resourceAssignments, addedResourceIds),
  };
}
